import React, { Suspense, lazy } from 'react';
import logoSpinner from './assets/fenerbahce-serefli-yili.png';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { StoreProvider } from './context/StoreContext';
import { AuthProvider } from './context/AuthContext';
import Navbar from './Navbar';
import ScrollToTop from './components/ScrollToTop';
import AnimatedColorBar from './components/AnimatedColorBar';
import DontMissBar from './components/Don_t_miss';
import Hero from './components/Hero';
import NewsSection from './components/NewsSection';
import SponsorsSection from './components/SponsorsSection';
import ProductCarousel from './components/ProductCarousel';
import TrophyAchievements from './components/TrophyAchievements';
import Footer from './components/Footer';
import PhotoGallery from './components/PhotoGallery';
import JoinCta from './components/JoinCta';
import HomeIntro from './components/HomeIntro';
import SEO from './components/SEO';
import './App.css';

// Admin panel — lazy loaded (not part of the public site bundle)
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const HeroManager = lazy(() => import('./pages/admin/sections/HeroManager'));
const NewsManager = lazy(() => import('./pages/admin/sections/NewsManager'));
const TrophiesManager = lazy(() => import('./pages/admin/sections/TrophiesManager'));
const GalleryManager = lazy(() => import('./pages/admin/sections/GalleryManager'));
const BoardManager = lazy(() => import('./pages/admin/sections/BoardManager'));
const UsersManager = lazy(() => import('./pages/admin/sections/UsersManager'));
const ProductsManager = lazy(() => import('./pages/admin/sections/ProductsManager'));
const SponsorsManager = lazy(() => import('./pages/admin/sections/SponsorsManager'));
const SettingsManager = lazy(() => import('./pages/admin/sections/SettingsManager'));
const MatchesAndTicketsSection = lazy(() => import('./pages/admin/sections/MatchesAndTicketsSection'));
const MembersManager = lazy(() => import('./pages/admin/sections/MembersManager'));

// Public pages — lazy loaded (split from main homepage bundle)
const MoreNews = lazy(() => import('./components/MoreNews'));
const NewsDetail = lazy(() => import('./components/NewsDetail'));
const AboutHero = lazy(() => import('./components/AboutHero'));
const AboutContent = lazy(() => import('./components/AboutContent'));
const ManagementBoard = lazy(() => import('./components/ManagementBoard'));
const SponsorshipPage = lazy(() => import('./components/SponsorshipPage'));
const ContactPage = lazy(() => import('./components/ContactPage'));
const MembersPage = lazy(() => import('./components/MembersPage'));
const UyelikPage = lazy(() => import('./components/UyelikPage'));
const AtaturkFbPage = lazy(() => import('./components/AtaturkFbPage'));
const StorePage = lazy(() => import('./components/StorePage'));
const MatchesPage = lazy(() => import('./components/MatchesPage'));
const ProductDetail = lazy(() => import('./components/ProductDetail'));
const NotFound = lazy(() => import('./components/NotFound'));

const LoadingFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#00205b',
  }}>
    <img
      src={logoSpinner}
      alt="Yükleniyor"
      style={{
        width: 72,
        height: 72,
        objectFit: 'contain',
        animation: 'page-logo-spin 1.1s linear infinite',
        opacity: 0.92,
      }}
    />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <StoreProvider>
          <BrowserRouter>
            <Routes>
              {/* Admin rotaları */}
              <Route path="/admin" element={<Suspense fallback={<LoadingFallback />}><AdminLogin /></Suspense>} />
              <Route path="/admin/dashboard" element={<Suspense fallback={<LoadingFallback />}><AdminLayout /></Suspense>}>
                <Route index element={<Navigate to="news" replace />} />
                <Route path="hero" element={<Suspense fallback={<LoadingFallback />}><HeroManager /></Suspense>} />
                <Route path="news" element={<Suspense fallback={<LoadingFallback />}><NewsManager /></Suspense>} />
                <Route path="gallery" element={<Suspense fallback={<LoadingFallback />}><GalleryManager /></Suspense>} />
                <Route path="trophies" element={<Suspense fallback={<LoadingFallback />}><TrophiesManager /></Suspense>} />
                <Route path="board" element={<Suspense fallback={<LoadingFallback />}><BoardManager /></Suspense>} />
                <Route path="products" element={<Suspense fallback={<LoadingFallback />}><ProductsManager /></Suspense>} />
                <Route path="sponsors" element={<Suspense fallback={<LoadingFallback />}><SponsorsManager /></Suspense>} />
                <Route path="matches" element={<Suspense fallback={<LoadingFallback />}><MatchesAndTicketsSection /></Suspense>} />
                <Route path="members" element={<Suspense fallback={<LoadingFallback />}><MembersManager /></Suspense>} />
                {/* Eski ayrı "Bilet Talepleri" sayfası artık "Maçlar" içinde bir sekme — bağlantı kırılmasın diye yönlendiriliyor. */}
                <Route path="tickets" element={<Navigate to="../matches" replace />} />
                <Route path="users" element={<Suspense fallback={<LoadingFallback />}><UsersManager /></Suspense>} />
                <Route path="settings" element={<Suspense fallback={<LoadingFallback />}><SettingsManager /></Suspense>} />
              </Route>

              {/* Ana site */}
              <Route path="/*" element={
                <div className="App">
                  <ScrollToTop />
                  <Navbar />
                  <AnimatedColorBar />
                  <main>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* Anasayfa */}
                  <Route path="/" element={
                    <>
                      <SEO
                        title="İskoçya Fenerbahçeliler Derneği | Fenerbahçe Scotland"
                        description="İskoçya Fenerbahçeliler Derneği (Fenerbahçe Scotland), Edinburgh merkezli olarak İskoçya'daki Fenerbahçelileri buluşturuyor. Üyelik, etkinlikler ve haberler."
                        keywords="İskoçya Fenerbahçeliler Derneği, Fenerbahçe Scotland, Fenerbahce Scotland, Fenerbahçe Edinburgh, Fenerbahçe taraftar derneği, İskoçya Türk derneği, Fenerbahçe İskoçya"
                        url="/"
                        image="/og-image.jpg"
                      />
                      <Hero />
                      <HomeIntro />
                      <NewsSection />
                      <TrophyAchievements />
                      <ProductCarousel />
                      <SponsorsSection />
                      {/* 6'şarlı, "Daha Fazla" butonuyla yüklenen galeri */}
                      <PhotoGallery />
                      <DontMissBar />
                      <JoinCta />
                    </>
                  } />

                  {/* Haberler */}
                  <Route path="/news" element={
                    <>
                      <SEO
                        title="Haberler - İskoçya Fenerbahçeliler Derneği"
                        description="İskoçya Fenerbahçeliler Derneği haberleri. Fenerbahçe ve dernek etkinlikleri hakkında en güncel haberler."
                        keywords="Fenerbahçe haberleri, İskoçya Fenerbahçe haberler, FB Scotland news, Fenerbahçe Scotland"
                        url="/news"
                        image="/og-image.jpg"
                      />
                      <MoreNews />
                    </>
                  } />

                  {/* Haber Detayı — SEO haberin kendi verisiyle NewsDetail içinde kurulur */}
                  <Route path="/news/:id" element={<NewsDetail />} />

                  {/* Derneğimiz */}
                  <Route path="/dernegimiz" element={
                    <>
                      <SEO
                        title="Derneğimiz - İskoçya Fenerbahçeliler Derneği"
                        description="İskoçya Fenerbahçeliler Derneği hakkında bilgi. Kuruluş amacı, yönetim kurulu, tarihçe ve Fenerbahçe'ye bağlılık."
                        keywords="İskoçya Fenerbahçeliler Derneği, dernek hakkında, kuruluş amacı, yönetim kurulu, Fenerbahçe Scotland"
                        url="/dernegimiz"
                        image="/og-image.jpg"
                      />
                      <AboutHero />
                      <AboutContent />
                      <ManagementBoard />
                      {/* 6'şarlı, "Daha Fazla" butonuyla yüklenen galeri */}
                      <PhotoGallery />
                      <JoinCta />
                    </>
                  } />

                  {/* İletişim */}
                  <Route path="/contact" element={
                    <>
                      <SEO
                        title="İletişim - İskoçya Fenerbahçeliler Derneği"
                        description="İskoçya Fenerbahçeliler Derneği iletişim bilgileri. Edinburgh adres, e-posta, telefon ve sosyal medya hesapları."
                        keywords="İskoçya Fenerbahçe iletişim, dernek adres Edinburgh, FB Scotland contact, fenerbahcescotland.org.uk"
                        url="/contact"
                        image="/og-image.jpg"
                      />
                      <ContactPage />
                    </>
                  } />

                  {/* Yönetim Kurulu */}
                  <Route path="/members" element={
                    <>
                      <SEO
                        title="Yönetim Kurulu - İskoçya Fenerbahçeliler Derneği"
                        description="İskoçya Fenerbahçeliler Derneği yönetim kurulu üyeleri. Dernek başkanı ve yöneticiler hakkında bilgi."
                        keywords="İskoçya Fenerbahçe yönetim kurulu, dernek başkanı, Fenerbahçe Scotland üyeler"
                        url="/members"
                        image="/og-image.jpg"
                      />
                      <MembersPage />
                    </>
                  } />

                  {/* Üyelik — SEO (form şeması ile birlikte) UyelikPage içinde kurulur */}
                  <Route path="/membership" element={<UyelikPage />} />

                  <Route path="/sponsorluk" element={<Suspense fallback={<LoadingFallback />}><SponsorshipPage /></Suspense>} />

                  {/* Atatürk & Fenerbahçe */}
                  <Route path="/ataturk-fenerbahce" element={
                    <>
                      <SEO
                        title="Atatürk & Fenerbahçe - İskoçya Fenerbahçeliler Derneği"
                        description="Atatürk ve Fenerbahçe'nin tarihi ilişkisi. 1918'den 1936'ya iki büyük değerin ortak hikayesi. Mustafa Kemal'in Fenerbahçe sevgisi."
                        keywords="Atatürk Fenerbahçe, Mustafa Kemal Fenerbahçe, tarihi ilişki, Fenerbahçe tarih, Atatürk spor"
                        url="/ataturk-fenerbahce"
                        image="/og-image.jpg"
                      />
                      <AtaturkFbPage />
                    </>
                  } />

                  {/* Mağaza */}
                  <Route path="/store" element={
                    <>
                      <SEO
                        title="Mağaza - İskoçya Fenerbahçeliler Derneği"
                        description="İskoçya Fenerbahçeliler Derneği mağazası. Fenerbahçe ürünleri ve bağış sistemi ile kulübünüze destek olun."
                        keywords="Fenerbahçe mağaza, dernek ürünleri, Fenerbahçe Scotland store, FB ürünleri bağış"
                        url="/store"
                        image="/og-image.jpg"
                      />
                      <StorePage />
                    </>
                  } />

                  {/* Maçlar — SEO MatchesPage içinde kurulur */}
                  <Route path="/maclar" element={<MatchesPage />} />

                  {/* Ürün Detayı — SEO ürünün kendi verisiyle ProductDetail içinde kurulur */}
                  <Route path="/store/:id" element={<ProductDetail />} />

                  {/* 404 — bilinmeyen adresler (arama motorlarına indekslenmez) */}
                  <Route path="*" element={
                    <>
                      <SEO
                        title="Sayfa Bulunamadı - İskoçya Fenerbahçeliler Derneği"
                        description="Aradığınız sayfa bulunamadı. Anasayfaya dönerek derneğimizin güncel içeriklerine ulaşabilirsiniz."
                        url="/"
                        noindex
                      />
                      <NotFound />
                    </>
                  } />
                      </Routes>
                    </Suspense>
                  </main>
                  <Footer />
                </div>
              } />
            </Routes>
          </BrowserRouter>
        </StoreProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
